import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Field, Input, Select, Textarea } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { CHANNEL_LABEL, SOURCE_LABEL } from '@/features/marketing/queries'
import { useCreateInquiry } from './queries'

/**
 * Logging an enquiry that arrived by phone, Instagram or word of mouth.
 *
 * The website form will post to an edge function and land in the same table, but
 * plenty of enquiries arrive in conversation and they should not be second-class
 * — an enquiry nobody bothers to log is a follow-up nobody makes.
 *
 * Only a name and an email are required. Everything else is what she happens to
 * know, and a half-filled enquiry beats a mental note.
 */
export function NewEnquiryPage() {
  const navigate = useNavigate()
  const create = useCreateInquiry()

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    partner_first_name: '',
    partner_last_name: '',
    event_date: '',
    guest_count: '',
    services: '',
    venue_text: '',
    heard_about: '',
    channel: '',
    lead_source: '',
    message: '',
  })

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const ready = form.first_name.trim() !== '' && form.email.trim() !== ''

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!ready) return

    const guests = form.guest_count.trim() === '' ? null : Number(form.guest_count)

    const inquiry = await create.mutateAsync({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim() || null,
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      partner_first_name: form.partner_first_name.trim() || null,
      partner_last_name: form.partner_last_name.trim() || null,
      event_date: form.event_date || null,
      guest_count: guests !== null && Number.isFinite(guests) ? Math.round(guests) : null,
      services: form.services ? (form.services as 'photography' | 'videography' | 'both') : null,
      venue_text: form.venue_text.trim() || null,
      heard_about: form.heard_about.trim() || null,
      channel: form.channel || null,
      lead_source: form.lead_source || null,
      message: form.message.trim() || null,
      // Records how this one arrived, so the website form and a phone call are
      // distinguishable later without guessing.
      raw_payload: { source: 'logged by staff' },
    })

    toast.success('Enquiry logged')
    void navigate(`/enquiries/${inquiry.id}`)
  }

  return (
    <Page>
      <form onSubmit={(event) => void submit(event)}>
        <PageHeader
          eyebrow="Enquiries"
          title="Log an enquiry"
          actions={
            <>
              <Button variant="ghost" onClick={() => void navigate('/enquiries')}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={create.isPending} loadingLabel="Saving" disabled={!ready}>
                Log enquiry
              </Button>
            </>
          }
        />

        <Section title="Who got in touch">
          <div className="grid max-w-3xl gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="First name" required>
              <Input value={form.first_name} onChange={(event) => set('first_name', event.target.value)} required autoFocus />
            </Field>
            <Field label="Last name">
              <Input value={form.last_name} onChange={(event) => set('last_name', event.target.value)} />
            </Field>
            <Field label="Email" required>
              <Input type="email" value={form.email} onChange={(event) => set('email', event.target.value)} required />
            </Field>
            <Field label="Phone">
              <Input type="tel" value={form.phone} onChange={(event) => set('phone', event.target.value)} />
            </Field>
            <Field label="Partner's first name">
              <Input value={form.partner_first_name} onChange={(event) => set('partner_first_name', event.target.value)} />
            </Field>
            <Field label="Partner's last name">
              <Input value={form.partner_last_name} onChange={(event) => set('partner_last_name', event.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="The wedding" description="Whatever they mentioned. A date can be added later.">
          <div className="grid max-w-3xl gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="Wedding date">
              <Input type="date" value={form.event_date} onChange={(event) => set('event_date', event.target.value)} />
            </Field>
            <Field label="Guests">
              <Input
                type="number"
                min="0"
                max="5000"
                value={form.guest_count}
                onChange={(event) => set('guest_count', event.target.value)}
              />
            </Field>
            <Field label="What they want">
              <Select value={form.services} onChange={(event) => set('services', event.target.value)}>
                <option value="">Not said</option>
                <option value="photography">Photography</option>
                <option value="videography">Film</option>
                <option value="both">Photo + film</option>
              </Select>
            </Field>
            <Field label="Venue" hint="As they said it. Match it to the library from the enquiry page.">
              <Input value={form.venue_text} onChange={(event) => set('venue_text', event.target.value)} />
            </Field>
            <Field label="How they heard about you">
              <Input value={form.heard_about} onChange={(event) => set('heard_about', event.target.value)} />
            </Field>
            <Field label="Came in by">
              <Select value={form.channel} onChange={(event) => set('channel', event.target.value)}>
                <option value="">Not recorded</option>
                {Object.entries(CHANNEL_LABEL).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="What brought them" hint="Counts towards what each kind of marketing brings in.">
              <Select value={form.lead_source} onChange={(event) => set('lead_source', event.target.value)}>
                <option value="">Not recorded</option>
                {Object.entries(SOURCE_LABEL).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="What they said">
          <Field label="Notes from the conversation" className="max-w-3xl">
            <Textarea rows={5} value={form.message} onChange={(event) => set('message', event.target.value)} />
          </Field>
        </Section>
      </form>
    </Page>
  )
}
